import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { formatDate, formatDateTime } from "../lib/date";
import { toast } from "sonner";
import {
  Building2,
  Ticket,
  Code,
  LogOut,
  Plus,
  MessageSquare,
  ExternalLink,
  RefreshCw,
  Clock,
  AlertCircle,
  CheckCircle,
  Search,
  Filter,
  Send,
  X,
  Loader2,
  KeyRound,
  Mail,
  ChevronRight,
  Shield
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter
} from "../components/ui/sheet";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientPortal() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Login state
  const [clientId, setClientId] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpGenerated, setOtpGenerated] = useState("");
  
  // Tickets state
  const [tickets, setTickets] = useState({ support_tickets: [], dev_tickets: [] });
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("support");
  
  // Create ticket dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTicketSummary, setNewTicketSummary] = useState("");
  const [newTicketDescription, setNewTicketDescription] = useState("");
  const [newTicketPriority, setNewTicketPriority] = useState("Medium");
  const [creatingTicket, setCreatingTicket] = useState(false);
  
  // Comment dialog
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [addingComment, setAddingComment] = useState(false);
  
  // Ticket detail view
  const [ticketDetailOpen, setTicketDetailOpen] = useState(false);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [loadingTicketDetail, setLoadingTicketDetail] = useState(false);

  // Set document title for Client Portal
  useEffect(() => {
    document.title = "EQUIPR | Client Portal";
    return () => {
      document.title = "EQUIPR | Intelligence";
    };
  }, []);

  // Check for existing session
  useEffect(() => {
    const savedSession = localStorage.getItem("equipr_client_session");
    if (savedSession) {
      setSession(JSON.parse(savedSession));
    }
  }, []);

  // Fetch tickets when logged in
  useEffect(() => {
    if (session) {
      fetchTickets();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const generateOtp = async () => {
    if (!clientId || !email) {
      toast.error("Please enter Client ID and Email");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/clients/generate-otp`, {
        client_id: clientId,
        email: email
      });
      
      setOtpGenerated(true);
      if (response.data.email_sent) {
        toast.success("Login code sent to your email!");
      } else {
        toast.success("OTP generated! Check your email.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!clientId || !email || !otp) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/clients/login`, {
        client_id: clientId,
        email: email,
        otp: otp
      });
      
      const sessionData = response.data.session;
      setSession(sessionData);
      localStorage.setItem("equipr_client_session", JSON.stringify(sessionData));
      toast.success(`Welcome, ${sessionData.company_name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setSession(null);
    localStorage.removeItem("equipr_client_session");
    setClientId("");
    setEmail("");
    setOtp("");
    setOtpGenerated(false);
    toast.info("Logged out successfully");
  };

  const fetchTickets = async () => {
    if (!session) return;
    
    setLoadingTickets(true);
    try {
      const response = await axios.get(`${API}/clients/${session.client_id}/tickets`);
      setTickets(response.data);
    } catch (error) {
      toast.error("Failed to fetch tickets");
    } finally {
      setLoadingTickets(false);
    }
  };

  const openTicketDetail = async (ticket) => {
    setTicketDetail(ticket);
    setTicketDetailOpen(true);
    setLoadingTicketDetail(true);
    
    try {
      // Fetch full ticket details including comments
      const response = await axios.get(`${API}/clients/${session.client_id}/tickets/${ticket.key}`);
      setTicketDetail(response.data);
    } catch (error) {
      console.error("Failed to fetch ticket details:", error);
      // Keep the basic ticket info if detail fetch fails
    } finally {
      setLoadingTicketDetail(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicketSummary) {
      toast.error("Please enter a summary");
      return;
    }
    
    setCreatingTicket(true);
    try {
      const response = await axios.post(`${API}/clients/${session.client_id}/tickets/create`, {
        summary: newTicketSummary,
        description: newTicketDescription,
        priority: newTicketPriority
      });
      
      toast.success(`Ticket ${response.data.ticket_key} created!`);
      setCreateDialogOpen(false);
      setNewTicketSummary("");
      setNewTicketDescription("");
      setNewTicketPriority("Medium");
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create ticket");
    } finally {
      setCreatingTicket(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText || !selectedTicket) {
      toast.error("Please enter a comment");
      return;
    }
    
    setAddingComment(true);
    try {
      await axios.post(`${API}/clients/${session.client_id}/tickets/${selectedTicket.key}/comment`, {
        body: commentText
      });
      
      toast.success("Comment added!");
      setCommentDialogOpen(false);
      setCommentText("");
      setSelectedTicket(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add comment");
    } finally {
      setAddingComment(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("resolved") || statusLower.includes("closed") || statusLower.includes("done")) {
      return "bg-[#30D158]/20 text-[#30D158]";
    }
    if (statusLower.includes("progress") || statusLower.includes("review")) {
      return "bg-[#007AFF]/20 text-[#007AFF]";
    }
    if (statusLower.includes("waiting")) {
      return "bg-[#FF9F0A]/20 text-[#FF9F0A]";
    }
    return "bg-[#A1A1AA]/20 text-[#A1A1AA]";
  };

  const getPriorityColor = (priority) => {
    const priorityLower = priority?.toLowerCase() || "";
    if (priorityLower.includes("highest") || priorityLower.includes("critical")) {
      return "bg-[#FF453A]/20 text-[#FF453A]";
    }
    if (priorityLower.includes("high")) {
      return "bg-[#FF9F0A]/20 text-[#FF9F0A]";
    }
    if (priorityLower.includes("low")) {
      return "bg-[#30D158]/20 text-[#30D158]";
    }
    return "bg-[#007AFF]/20 text-[#007AFF]";
  };

  const filteredTickets = useCallback((ticketList) => {
    if (!searchTerm) return ticketList;
    const term = searchTerm.toLowerCase();
    return ticketList.filter(t => 
      t.key?.toLowerCase().includes(term) ||
      t.summary?.toLowerCase().includes(term) ||
      t.status?.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  // Login Screen
  if (!session) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex">
        {/* Left Panel - Login Form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md space-y-8">
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Building2 className="w-10 h-10 text-[#007AFF]" />
                <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  EQUIPR CLIENT PORTAL
                </h1>
              </div>
              <p className="text-[#A1A1AA]">
                Access your support and development tickets
              </p>
            </div>

            <Card className="bg-[#111111] border-[#27272A]">
              <CardContent className="p-6 space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Client ID</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                    <Input
                      type="text"
                      placeholder="e.g., EAG123"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value.toUpperCase())}
                      className="bg-[#1A1A1A] border-[#27272A] text-white pl-10"
                      data-testid="client-id-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.toLowerCase())}
                      className="bg-[#1A1A1A] border-[#27272A] text-white pl-10"
                      data-testid="client-email-input"
                    />
                  </div>
                </div>

                {!otpGenerated ? (
                  <Button
                    onClick={generateOtp}
                    disabled={loading || !clientId || !email}
                    className="w-full bg-[#007AFF] hover:bg-[#007AFF]/90 text-white"
                    data-testid="generate-otp-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <ChevronRight className="w-4 h-4 mr-2" />
                    )}
                    Generate OTP
                  </Button>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label className="text-white">One-Time Password</Label>
                      <Input
                        type="text"
                        placeholder="4-digit code"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        maxLength={4}
                        className="bg-[#1A1A1A] border-[#27272A] text-white text-center text-2xl tracking-widest"
                        data-testid="client-otp-input"
                      />
                    </div>

                    <Button
                      onClick={handleLogin}
                      disabled={loading || !otp}
                      className="w-full bg-[#30D158] hover:bg-[#30D158]/90 text-white"
                      data-testid="client-login-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <LogOut className="w-4 h-4 mr-2 rotate-180" />
                      )}
                      Login
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Right Panel - Login Info */}
        <div className="w-80 bg-[#111111] border-l border-[#27272A] p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#007AFF]" />
            Secure Login
          </h2>
          
          <div className="bg-[#1A1A1A] rounded-lg p-4 mb-4">
            <h3 className="text-sm font-medium text-white mb-3">How to Login</h3>
            <ol className="text-xs text-[#A1A1AA] space-y-2">
              <li className="flex items-start gap-2">
                <span className="bg-[#007AFF] text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] shrink-0 mt-0.5">1</span>
                <span>Enter your Client ID and email address</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-[#007AFF] text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] shrink-0 mt-0.5">2</span>
                <span>Click "Get OTP" to receive your login code via email</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-[#007AFF] text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] shrink-0 mt-0.5">3</span>
                <span>Enter the 6-digit code from your email</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-[#007AFF] text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] shrink-0 mt-0.5">4</span>
                <span>Click "Login" to access your portal</span>
              </li>
            </ol>
          </div>
          
          <div className="p-3 bg-[#30D158]/10 rounded-lg border border-[#30D158]/30">
            <p className="text-xs text-[#30D158] text-center">
              <Mail className="w-3 h-3 inline mr-1" />
              OTP codes are sent to your registered email
            </p>
          </div>
          
          <div className="mt-4 p-3 bg-[#FF9F0A]/10 rounded-lg border border-[#FF9F0A]/30">
            <p className="text-xs text-[#FF9F0A] text-center">
              Codes expire after 10 minutes
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#111111] border-b border-[#27272A] px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Building2 className="w-8 h-8 text-[#007AFF]" />
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {session.company_name}
              </h1>
              <p className="text-xs text-[#A1A1AA]">Client Portal • {session.client_id}</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Button
              onClick={() => setCreateDialogOpen(true)}
              className="bg-[#30D158] hover:bg-[#30D158]/90 text-white"
              data-testid="create-ticket-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Support Ticket
            </Button>
            
            <Button
              variant="ghost"
              onClick={fetchTickets}
              disabled={loadingTickets}
              className="text-[#A1A1AA] hover:text-white"
              data-testid="refresh-tickets-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loadingTickets ? 'animate-spin' : ''}`} />
            </Button>
            
            <div className="flex items-center gap-3 pl-4 border-l border-[#27272A]">
              <div className="text-right">
                <p className="text-sm font-medium text-white">{session.contact_name || session.email}</p>
                <p className="text-xs text-[#A1A1AA]">{session.email}</p>
              </div>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-[#A1A1AA] hover:text-white"
                data-testid="client-logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="bg-[#111111] border-[#27272A]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#007AFF]/20 rounded-lg">
                  <Ticket className="w-5 h-5 text-[#007AFF]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{tickets.total_support || 0}</p>
                  <p className="text-xs text-[#A1A1AA]">Support Tickets</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#111111] border-[#27272A]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#AF52DE]/20 rounded-lg">
                  <Code className="w-5 h-5 text-[#AF52DE]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{tickets.total_dev || 0}</p>
                  <p className="text-xs text-[#A1A1AA]">Development Tickets</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#111111] border-[#27272A]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#FF9F0A]/20 rounded-lg">
                  <Clock className="w-5 h-5 text-[#FF9F0A]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {tickets.support_tickets?.filter(t => 
                      !t.status?.toLowerCase().includes("resolved") && 
                      !t.status?.toLowerCase().includes("closed")
                    ).length || 0}
                  </p>
                  <p className="text-xs text-[#A1A1AA]">Open Support</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#111111] border-[#27272A]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#30D158]/20 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-[#30D158]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {tickets.support_tickets?.filter(t => 
                      t.status?.toLowerCase().includes("resolved") || 
                      t.status?.toLowerCase().includes("closed")
                    ).length || 0}
                  </p>
                  <p className="text-xs text-[#A1A1AA]">Resolved</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input
              type="text"
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-[#111111] border-[#27272A] text-white pl-10"
              data-testid="search-tickets-input"
            />
          </div>
        </div>

        {/* Tickets Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#111111] border border-[#27272A] mb-4">
            <TabsTrigger value="support" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white">
              <Ticket className="w-4 h-4 mr-2" />
              Support Tickets ({tickets.total_support || 0})
            </TabsTrigger>
            <TabsTrigger value="dev" className="data-[state=active]:bg-[#AF52DE] data-[state=active]:text-white">
              <Code className="w-4 h-4 mr-2" />
              Development Tickets ({tickets.total_dev || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="support">
            <Card className="bg-[#111111] border-[#27272A]">
              <CardContent className="p-0">
                {loadingTickets ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
                  </div>
                ) : filteredTickets(tickets.support_tickets || []).length === 0 ? (
                  <div className="text-center py-12">
                    <Ticket className="w-12 h-12 text-[#27272A] mx-auto mb-3" />
                    <p className="text-[#A1A1AA]">No support tickets found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#27272A]">
                    {filteredTickets(tickets.support_tickets || []).map((ticket) => (
                      <div key={ticket.key} className="p-4 hover:bg-[#1A1A1A] transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 cursor-pointer" onClick={() => openTicketDetail(ticket)}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[#007AFF] font-medium hover:underline flex items-center gap-1">
                                {ticket.key}
                                <ChevronRight className="w-3 h-3" />
                              </span>
                              <Badge className={getStatusColor(ticket.status)}>
                                {ticket.status}
                              </Badge>
                              <Badge className={getPriorityColor(ticket.priority)}>
                                {ticket.priority}
                              </Badge>
                            </div>
                            <p className="text-white">{ticket.summary}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-[#A1A1AA]">
                              <span>Assignee: {ticket.assignee || "Unassigned"}</span>
                              <span>Updated: {formatDate(ticket.updated)}</span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedTicket(ticket);
                              setCommentDialogOpen(true);
                            }}
                            className="text-[#A1A1AA] hover:text-white"
                            data-testid={`comment-btn-${ticket.key}`}
                          >
                            <MessageSquare className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="dev">
            <Card className="bg-[#111111] border-[#27272A]">
              <CardContent className="p-0">
                {loadingTickets ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#AF52DE]" />
                  </div>
                ) : filteredTickets(tickets.dev_tickets || []).length === 0 ? (
                  <div className="text-center py-12">
                    <Code className="w-12 h-12 text-[#27272A] mx-auto mb-3" />
                    <p className="text-[#A1A1AA]">No development tickets found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#27272A]">
                    {filteredTickets(tickets.dev_tickets || []).map((ticket) => (
                      <div key={ticket.key} className="p-4 hover:bg-[#1A1A1A] transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 cursor-pointer" onClick={() => openTicketDetail(ticket)}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[#AF52DE] font-medium hover:underline flex items-center gap-1">
                                {ticket.key}
                                <ChevronRight className="w-3 h-3" />
                              </span>
                              <Badge className={getStatusColor(ticket.status)}>
                                {ticket.status}
                              </Badge>
                              <Badge className={getPriorityColor(ticket.priority)}>
                                {ticket.priority}
                              </Badge>
                            </div>
                            <p className="text-white">{ticket.summary}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-[#A1A1AA]">
                              <span>Assignee: {ticket.assignee || "Unassigned"}</span>
                              <span>Updated: {formatDate(ticket.updated)}</span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedTicket(ticket);
                              setCommentDialogOpen(true);
                            }}
                            className="text-[#A1A1AA] hover:text-white"
                            data-testid={`comment-btn-${ticket.key}`}
                          >
                            <MessageSquare className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Ticket Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-[#111111] border-[#27272A] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#30D158]" />
              Create Support Ticket
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Summary *</Label>
              <Input
                type="text"
                placeholder="Brief description of the issue"
                value={newTicketSummary}
                onChange={(e) => setNewTicketSummary(e.target.value)}
                className="bg-[#1A1A1A] border-[#27272A] text-white"
                data-testid="new-ticket-summary"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Description</Label>
              <Textarea
                placeholder="Detailed description of the issue..."
                value={newTicketDescription}
                onChange={(e) => setNewTicketDescription(e.target.value)}
                className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[120px]"
                data-testid="new-ticket-description"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Priority</Label>
              <Select value={newTicketPriority} onValueChange={setNewTicketPriority}>
                <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-white" data-testid="new-ticket-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                  <SelectItem value="Highest" className="text-white">Highest - Critical</SelectItem>
                  <SelectItem value="High" className="text-white">High - Urgent</SelectItem>
                  <SelectItem value="Medium" className="text-white">Medium - Normal</SelectItem>
                  <SelectItem value="Low" className="text-white">Low - Minor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              className="border-[#27272A] text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTicket}
              disabled={creatingTicket || !newTicketSummary}
              className="bg-[#30D158] hover:bg-[#30D158]/90 text-white"
              data-testid="submit-ticket-btn"
            >
              {creatingTicket ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Create Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent className="bg-[#111111] border-[#27272A] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#007AFF]" />
              Add Comment to {selectedTicket?.key}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-[#A1A1AA]">{selectedTicket?.summary}</p>
            
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Your Comment</Label>
              <Textarea
                placeholder="Enter your comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                className="bg-[#1A1A1A] border-[#27272A] text-white min-h-[120px]"
                data-testid="comment-text"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCommentDialogOpen(false)}
              className="border-[#27272A] text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddComment}
              disabled={addingComment || !commentText}
              className="bg-[#007AFF] hover:bg-[#007AFF]/90 text-white"
              data-testid="submit-comment-btn"
            >
              {addingComment ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Add Comment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Sheet - Slides from Right */}
      <Sheet open={ticketDetailOpen} onOpenChange={setTicketDetailOpen}>
        <SheetContent side="right" className="bg-[#111111] border-[#27272A] text-white w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader className="pb-4 border-b border-[#27272A]">
            <SheetTitle className="text-xl text-white flex items-center gap-2">
              <Ticket className="w-5 h-5 text-[#007AFF]" />
              {ticketDetail?.key}
            </SheetTitle>
          </SheetHeader>
          
          {loadingTicketDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
            </div>
          ) : ticketDetail ? (
            <div className="space-y-6 py-6">
              {/* Status & Priority */}
              <div className="flex items-center gap-2">
                <Badge className={getStatusColor(ticketDetail.status)}>
                  {ticketDetail.status}
                </Badge>
                <Badge className={getPriorityColor(ticketDetail.priority)}>
                  {ticketDetail.priority}
                </Badge>
              </div>
              
              {/* Summary */}
              <div>
                <h3 className="text-sm font-medium text-[#71717A] mb-1">Summary</h3>
                <p className="text-white">{ticketDetail.summary}</p>
              </div>
              
              {/* Description */}
              {ticketDetail.description && (
                <div>
                  <h3 className="text-sm font-medium text-[#71717A] mb-2">Description</h3>
                  <div className="bg-[#1A1A1A] p-4 rounded-lg border border-[#27272A]">
                    <p className="text-[#A1A1AA] text-sm whitespace-pre-wrap">{ticketDetail.description}</p>
                  </div>
                </div>
              )}
              
              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm bg-[#1A1A1A] p-4 rounded-lg">
                <div>
                  <span className="text-[#71717A] block">Assignee</span>
                  <span className="text-white">{ticketDetail.assignee || "Unassigned"}</span>
                </div>
                <div>
                  <span className="text-[#71717A] block">Reporter</span>
                  <span className="text-white">{ticketDetail.reporter || "Unknown"}</span>
                </div>
                <div>
                  <span className="text-[#71717A] block">Created</span>
                  <span className="text-white">
                    {ticketDetail.created ? formatDate(ticketDetail.created) : "Unknown"}
                  </span>
                </div>
                <div>
                  <span className="text-[#71717A] block">Updated</span>
                  <span className="text-white">
                    {ticketDetail.updated ? formatDate(ticketDetail.updated) : "Unknown"}
                  </span>
                </div>
              </div>
              
              {/* Comments Section - Filter out internal notes */}
              {ticketDetail.comments && ticketDetail.comments.filter(c => !c.internal).length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-[#71717A] mb-3">
                    Comments ({ticketDetail.comments.filter(c => !c.internal).length})
                  </h3>
                  <div className="space-y-3">
                    {ticketDetail.comments
                      .filter(comment => !comment.internal)
                      .map((comment, index) => (
                        <div key={index} className="bg-[#1A1A1A] p-4 rounded-lg border border-[#27272A]">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-white">{comment.author || "Unknown"}</span>
                            <span className="text-xs text-[#71717A]">
                              {comment.created ? formatDateTime(comment.created) : ""}
                            </span>
                          </div>
                          <p className="text-[#A1A1AA] text-sm whitespace-pre-wrap">{comment.body}</p>
                        </div>
                      ))}
                  </div>
                </div>
              )}
              
              {/* Add Comment Button */}
              <div className="pt-4 border-t border-[#27272A]">
                <Button
                  onClick={() => {
                    setSelectedTicket(ticketDetail);
                    setCommentDialogOpen(true);
                    setTicketDetailOpen(false);
                  }}
                  className="w-full bg-[#007AFF] hover:bg-[#007AFF]/90 text-white"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Add Comment
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-[#27272A] mx-auto mb-3" />
              <p className="text-[#A1A1AA]">Failed to load ticket details</p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
